import { memo, useCallback, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { Tooltip as BaseTooltip } from '@base-ui/react/tooltip';
import { Tooltip } from '@components/ui/Tooltip/Tooltip';
import { KebabButton } from '@components/ui/KebabButton/KebabButton';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from '@utils/icons';
import { selectSiteTz } from '@store/selectors/site.selectors';
import { fetchSchedules, fetchSchedule } from '@apis/schedule';
import { scheduleListKey, scheduleKey } from '@constants/queryKeys';
import { useActiveScheduleNumber } from '@hooks/useActiveSchedule';
import type {
  IMasterScheduleModel,
  IMasterScheduleRow,
  MasterScheduleGranularity,
  MasterScheduleStatusKind,
} from '@interfaces/IMasterSchedule';
import { MilestoneTooltip } from '@components/ui/MilestoneTooltip/MilestoneTooltip';
import { ZoomSwitch } from '@components/ui/ZoomSwitch/ZoomSwitch';
import { PX_PER_DAY as SCHEDULER_PX_PER_DAY } from '../../SchedulerPage/lib/timeScale';
import { deriveMasterSchedule } from './masterSchedule.derive';
import { tickAnchorsMs } from './masterScheduleAxis';
import { useMasterScheduleCollapse } from './useMasterScheduleCollapse';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { useTimelineScrollSync } from './useTimelineScrollSync';
import { projectTodayMs, scheduleRetry } from './_shared/scheduleQueries';
import { DashboardCard } from './_shared/DashboardCard';
import { MasterScheduleShimmer } from '../DashboardSkeleton';
import styles from './masterScheduleCard.module.css';

/* Master Schedule — full-width "Critical Path Analysis" Gantt. Reads the
   project's Scheduler (工程表): the most-recent schedule's main activities
   become Gantt rows positioned against a whole-month timeline window; each
   row's status compares its actual rollup with the planned (date-derived)
   rollup at JST-today. Data is the live `/api/schedule` endpoint (shared
   query cache with the Scheduler page and ProjectProgressCard). */

/* Legend rows resolved via `t()` at render time — keys live under
   `dashboard.masterSchedule.legend*` for both en + ja locales. */
const LEGEND: { i18nKey: string; cls: 'dotCritical' | 'dotOnTrack' | 'dotMilestone' }[] = [
  { i18nKey: 'dashboard.masterSchedule.legendCritical',  cls: 'dotCritical'  },
  { i18nKey: 'dashboard.masterSchedule.legendOnTrack',   cls: 'dotOnTrack'   },
  { i18nKey: 'dashboard.masterSchedule.legendMilestone', cls: 'dotMilestone' },
];

/** Timeline granularities, ordered for the switch. */
const GRANULARITIES = ['day', 'week', 'month'] as const;

/** px-per-day per granularity. day/week reuse the scheduler's tuned values
 *  (imported so they can't silently drift); month is intentionally smaller —
 *  the card scrolls horizontally, so a roomy ~120px/month is fine. */
const PX_PER_DAY: Record<MasterScheduleGranularity, number> = { ...SCHEDULER_PX_PER_DAY, month: 4 };

export function MasterScheduleCard() {
  const { t } = useTranslation();
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  // Axis granularity (day/week/month). Lifted here so it survives a body
  // remount; defaults to the whole-month fit view.
  const [granularity, setGranularity] = useState<MasterScheduleGranularity>('month');

  // Two-step: list the site's schedules, then load the most-recent one. Same
  // query keys as the Scheduler page, so the cache is shared.
  const schedulesQuery = useQuery({
    queryKey: scheduleListKey(siteId ?? ''),
    queryFn: ({ signal }) => fetchSchedules(siteId!, signal),
    enabled: Boolean(siteId),
    staleTime: 5 * 60_000,
    retry: scheduleRetry,
  });
  // The schedule the user picked in the TopBar (shared across every surface);
  // falls back to the most-recent schedule when nothing is selected.
  const scheduleNumber = useActiveScheduleNumber(siteId, schedulesQuery.data ?? []);
  const scheduleQuery = useQuery({
    queryKey: scheduleKey(siteId ?? '', scheduleNumber),
    queryFn: ({ signal }) => fetchSchedule(siteId!, scheduleNumber!, signal),
    enabled: Boolean(siteId) && Boolean(scheduleNumber),
    staleTime: Infinity,
    retry: scheduleRetry,
  });

  /* The project's own zone decides which day is "today" — a site east of the
     viewer has already rolled over. No default zone: with none resolved yet the
     derivation is skipped rather than dated against a guess. */
  const tz = useSelector(selectSiteTz);
  const data = scheduleQuery.data;
  const isLoading = schedulesQuery.isLoading || scheduleQuery.isLoading;
  const isError = schedulesQuery.isError || scheduleQuery.isError;
  // Null when there's no schedule OR the schedule has no datable activities —
  // either way there is no timeline to draw. Memoized so toggling granularity /
  // collapsing groups (both re-render this component) don't re-derive the whole
  // model — derivation is view-independent.
  const model = useMemo(
    () => (data && tz ? deriveMasterSchedule(data, projectTodayMs(tz)) : null),
    [data, tz],
  );

  // Clicking a bar/diamond opens the Scheduler for the active schedule, focused
  // where that activity/milestone starts (taskId selects + scrolls to it; the
  // date scrolls there when there's no task, e.g. a group bar).
  const focusInScheduler = useCallback<FocusInScheduler>(
    ({ taskId, groupId, dateIso }) => {
      if (!siteId) return;
      const base = scheduleNumber
        ? `/scheduler/${encodeURIComponent(siteId)}/${encodeURIComponent(scheduleNumber)}`
        : `/scheduler/${encodeURIComponent(siteId)}`;
      const params = new URLSearchParams();
      if (taskId) params.set('focusTask', taskId);
      if (groupId) params.set('focusGroup', groupId);
      if (dateIso) params.set('focusDate', dateIso);
      const qs = params.toString();
      navigate(qs ? `${base}?${qs}` : base);
    },
    [navigate, siteId, scheduleNumber],
  );

  return (
    <DashboardCard
      title={t('dashboard.masterSchedule.title')}
      subtitle={t('dashboard.masterSchedule.subtitle')}
      headerRight={
        <>
          <ZoomSwitch
            value={granularity}
            options={GRANULARITIES}
            getLabel={(g) => t(`scheduler.toolbar.zoom${g[0]!.toUpperCase()}${g.slice(1)}`)}
            ariaLabel={t('dashboard.masterSchedule.granularity')}
            onChange={setGranularity}
          />
          <ul className={styles.legend}>
            {LEGEND.map((item) => (
              <li key={item.i18nKey} className={styles.legendItem}>
                <span className={`${styles.legendDot} ${styles[item.cls]}`} aria-hidden />
                {t(item.i18nKey)}
              </li>
            ))}
          </ul>
          <KebabButton className={styles.dotsButton} aria-label={t('dashboard.aiAgent.moreOptions')} disabled />
        </>
      }
      bodyClassName={styles.body}
    >
      {isLoading ? (
        <MasterScheduleShimmer />
      ) : isError ? (
        <div className={styles.stateMsg}>{t('dashboard.masterSchedule.loadFailed')}</div>
      ) : !model ? (
        <div className={styles.stateMsg}>{t('dashboard.masterSchedule.noSchedule')}</div>
      ) : (
        <MasterScheduleBody model={model} granularity={granularity} onFocus={focusInScheduler} />
      )}
    </DashboardCard>
  );
}

/** Row status chip text, localized per statusKind. Delayed rows carry the
 *  shortfall against plan so the chip says how far behind they are. */
function rowStatusText(row: IMasterScheduleRow, t: (key: string, opts?: Record<string, unknown>) => string): string {
  if (row.statusKind === 'delayed') {
    return t('dashboard.masterSchedule.statusDelayed', { value: Math.abs(row.variancePct) });
  }
  if (row.statusKind === 'on-track') return t('dashboard.masterSchedule.statusOnTrack');
  return t('dashboard.masterSchedule.statusPending');
}

/** Status chip colour class — always a string so it can never leak the literal
 *  word "undefined" into the DOM className. */
function rowStatusClass(kind: MasterScheduleStatusKind): string {
  if (kind === 'delayed') return styles.rowStatusDelayed ?? '';
  if (kind === 'on-track') return styles.rowStatusOnTrack ?? '';
  return styles.rowStatusPending ?? '';
}

/** Bar colour class for a row's status (always a string). */
function rowBarClass(kind: MasterScheduleStatusKind): string {
  if (kind === 'delayed') return styles.barCritical ?? '';
  if (kind === 'on-track') return styles.barOnTrack ?? '';
  return styles.barPending ?? '';
}

interface IRowNameProps {
  name: string;
}

/** Activity-name label that reveals the full name in a tooltip on hover/focus,
 *  but ONLY when the name is actually clipped by the ellipsis (no redundant
 *  tooltip when it already fits). Reuses the shared milestone tooltip styles.
 *  Relies on the surrounding <Tooltip.Provider> wrapping the rows. */
function RowName({ name }: IRowNameProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  return (
    <Tooltip
      variant="marker"
      wrap
      side="top"
      align="start"
      sideOffset={6}
      content={name}
      open={open}
      onOpenChange={(next) => {
        // Suppress opening when the label isn't truncated. Checked at hover
        // time rather than on mount, so it stays right across resizes.
        if (next && ref.current && ref.current.scrollWidth <= ref.current.clientWidth) return;
        setOpen(next);
      }}
    >
      <span ref={ref} className={styles.rowName}>{name}</span>
    </Tooltip>
  );
}

/** What to focus in the Scheduler: a task, or a group (resolved to its earliest
 *  task there), with the item's start date for horizontal positioning. */
interface IFocusTarget {
  taskId: string | null;
  groupId: string | null;
  dateIso: string | null;
}
type FocusInScheduler = (target: IFocusTarget) => void;

/** A row's focus target: summary rows carry a taskId; group rows (taskId null)
 *  carry their group id (row.key) so the Scheduler can resolve a real task. */
function rowFocusTarget(row: IMasterScheduleRow): IFocusTarget {
  return row.taskId
    ? { taskId: row.taskId, groupId: null, dateIso: row.startIso }
    : { taskId: null, groupId: row.key, dateIso: row.startIso };
}

/** onKeyDown that activates a role="button" element on Enter/Space. */
function keyActivate(action: () => void) {
  return (event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      action();
    }
  };
}

interface ITrackRowProps {
  row: IMasterScheduleRow;
  /** Open the clicked bar/diamond in the Scheduler at where it starts. */
  onFocus: FocusInScheduler;
  /** Build the "open in scheduler" accessible label for an item name. */
  getOpenLabel: (name: string) => string;
}

/** One timeline row (bar + diamonds), memoized. Bar/milestone positions are
 *  `frac`-based — unaffected by granularity (only the track's CSS width changes)
 *  — and `row` is referentially stable (the model is memoized), so this never
 *  re-renders on a date-swap and only the toggled row re-renders on collapse.
 *  That keeps the heavy Base UI milestone tooltips from re-mounting per click.
 *  Clicking a bar/diamond deep-links to the Scheduler at the item's start. */
const TrackRow = memo(function TrackRow({ row, onFocus, getOpenLabel }: ITrackRowProps) {
  return (
    <div className={styles.trackRow}>
      <div className={styles.track}>
        {row.startFrac !== null && row.endFrac !== null && (
          <div
            className={`${styles.bar} ${rowBarClass(row.statusKind)}`}
            style={{ left: `${row.startFrac * 100}%`, width: `${(row.endFrac - row.startFrac) * 100}%` }}
            role="button"
            tabIndex={0}
            aria-label={getOpenLabel(row.name)}
            onClick={() => onFocus(rowFocusTarget(row))}
            onKeyDown={keyActivate(() => onFocus(rowFocusTarget(row)))}
          >
            <span className={styles.barLabel}>{row.name}</span>
          </div>
        )}
        {row.milestones.map((milestone) => (
          <MilestoneTooltip key={milestone.key} name={milestone.name} dateIso={milestone.dateIso}>
            <span
              className={styles.milestone}
              style={{ left: `${milestone.frac * 100}%` }}
              role="button"
              tabIndex={0}
              aria-label={getOpenLabel(milestone.name)}
              onClick={() => onFocus({ taskId: milestone.key, groupId: null, dateIso: milestone.dateIso })}
              onKeyDown={keyActivate(() => onFocus({ taskId: milestone.key, groupId: null, dateIso: milestone.dateIso }))}
            />
          </MilestoneTooltip>
        ))}
      </div>
    </div>
  );
});

interface IFrozenRowProps {
  row: IMasterScheduleRow;
  collapsible: boolean;
  collapsed: boolean;
  statusText: string;
  collapseLabel: string;
  expandLabel: string;
  onToggle: (key: string) => void;
}

/** One frozen group-list row (chevron + name + status + %), memoized for the
 *  same reason as TrackRow. Strings are passed pre-resolved and `onToggle` is
 *  stable so only the row whose `collapsed` actually flips re-renders. */
const FrozenRow = memo(function FrozenRow({
  row,
  collapsible,
  collapsed,
  statusText,
  collapseLabel,
  expandLabel,
  onToggle,
}: IFrozenRowProps) {
  return (
    <div className={`${styles.frozenRow} ${row.depth > 0 ? styles.rowNested : ''}`}>
      <div className={styles.rowLabel} style={{ '--ms-depth': row.depth } as React.CSSProperties}>
        {collapsible ? (
          <button
            type="button"
            className={`${styles.chevron} ${collapsed ? styles.chevronCollapsed : ''}`}
            aria-expanded={!collapsed}
            aria-label={collapsed ? expandLabel : collapseLabel}
            onClick={() => onToggle(row.key)}
          >
            <ChevronDownIcon size={12} />
          </button>
        ) : (
          <span className={styles.chevronSpacer} aria-hidden />
        )}
        <div className={styles.rowLabelText}>
          <RowName name={row.name} />
          <span className={`${styles.rowStatus} ${rowStatusClass(row.statusKind)}`}>{statusText}</span>
        </div>
      </div>
      <span className={styles.rowPct}>{row.progressPct}%</span>
    </div>
  );
});

interface IMasterScheduleBodyProps {
  model: IMasterScheduleModel;
  granularity: MasterScheduleGranularity;
  /** Deep-link a clicked bar/diamond to the Scheduler at where it starts. */
  onFocus: FocusInScheduler;
}

/** The populated Gantt body — period + tick axis, activity rows, today line.
 *  Rendered only when a schedule with a derivable timeline has loaded. */
function MasterScheduleBody({ model, granularity, onFocus }: IMasterScheduleBodyProps) {
  const { t, i18n } = useTranslation();
  const getOpenLabel = useCallback(
    (name: string) => t('dashboard.masterSchedule.openInScheduler', { name }),
    [t],
  );
  const locale = (i18n.language ?? 'en').startsWith('ja') ? 'ja-JP' : 'en-US';
  const span = model.windowEndMs - model.windowStartMs;

  // Dates are plain calendar days parsed as UTC, so format in UTC (same
  // reasoning as ForecastRibbon): no client-timezone drift on the label.
  const monthFmt = useMemo(
    () => new Intl.DateTimeFormat(locale, { month: 'short', timeZone: 'UTC' }),
    [locale],
  );
  const dayFmt = useMemo(
    () => new Intl.DateTimeFormat(locale, { day: 'numeric', timeZone: 'UTC' }),
    [locale],
  );
  const yearFmt = useMemo(
    () => new Intl.DateTimeFormat(locale, { year: 'numeric', timeZone: 'UTC' }),
    [locale],
  );
  // Locale-correct month+year — "Jan 2026" in en, "2026年1月" in ja (Intl orders
  // the parts per locale, unlike a manual `${month} ${year}` concatenation).
  const monthYearFmt = useMemo(
    () => new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'short', timeZone: 'UTC' }),
    [locale],
  );

  /** Fraction (0..1) of a timestamp within the timeline window. */
  const frac = useCallback((ms: number) => (ms - model.windowStartMs) / span, [model.windowStartMs, span]);

  // Axis ticks — anchors → { ms, frac, label }. Month → month name; day/week → day number.
  const ticks = useMemo(() => {
    const anchors = tickAnchorsMs(model.windowStartMs, model.windowEndMs, model.monthStartsMs, granularity);
    const fmt = granularity === 'month' ? monthFmt : dayFmt;
    return anchors.map((ms) => ({ ms, frac: frac(ms), label: fmt.format(ms) }));
  }, [model.windowStartMs, model.windowEndMs, model.monthStartsMs, granularity, monthFmt, dayFmt, frac]);

  // Day view gets an extra band naming each week by its number WITHIN the month
  // (W1…W5, reset every month — Math.ceil(dayOfMonth / 7)), at the week starts.
  const weekBandEls = useMemo(() => {
    if (granularity !== 'day') return null;
    const weeks = tickAnchorsMs(model.windowStartMs, model.windowEndMs, model.monthStartsMs, 'week');
    return weeks.map((ms) => (
      <span key={ms} className={styles.weekLabel} style={{ left: `${frac(ms) * 100}%` }}>
        {t('dashboard.masterSchedule.weekShort', { n: Math.ceil(new Date(ms).getUTCDate() / 7) })}
      </span>
    ));
  }, [granularity, model.windowStartMs, model.windowEndMs, model.monthStartsMs, frac, t]);

  // Coarse "period" header band — one level above the ticks for context:
  // month view groups by YEAR; week + day views group by MONTH (January / the
  // first segment carry the year so multi-year ranges stay unambiguous).
  const periods = useMemo(() => {
    const out: { key: string; startFrac: number; endFrac: number; label: string }[] = [];
    if (granularity === 'month') {
      for (const ms of model.monthStartsMs) {
        const year = new Date(ms).getUTCFullYear();
        if (out.length && out[out.length - 1]!.key === String(year)) continue;
        out.push({
          key: String(year),
          startFrac: frac(Math.max(Date.UTC(year, 0, 1), model.windowStartMs)),
          endFrac: frac(Math.min(Date.UTC(year + 1, 0, 1), model.windowEndMs)),
          label: yearFmt.format(ms),
        });
      }
    } else {
      for (let i = 0; i < model.monthStartsMs.length; i++) {
        const ms = model.monthStartsMs[i]!;
        const next = i + 1 < model.monthStartsMs.length ? model.monthStartsMs[i + 1]! : model.windowEndMs;
        const showYear = i === 0 || new Date(ms).getUTCMonth() === 0;
        out.push({
          key: String(ms),
          startFrac: frac(Math.max(ms, model.windowStartMs)),
          endFrac: frac(Math.min(next, model.windowEndMs)),
          label: showYear ? monthYearFmt.format(ms) : monthFmt.format(ms),
        });
      }
    }
    return out;
  }, [granularity, model.monthStartsMs, model.windowStartMs, model.windowEndMs, monthFmt, monthYearFmt, yearFmt, frac]);

  // Coarse gridlines (body) — the next-coarser boundaries: week view → MONTH
  // lines, day view → WEEK lines (month view → year lines for consistency).
  const coarseAnchors = useMemo(() => {
    if (granularity === 'week') return model.monthStartsMs;
    if (granularity === 'day') {
      return tickAnchorsMs(model.windowStartMs, model.windowEndMs, model.monthStartsMs, 'week');
    }
    const out: number[] = [];
    const firstYear = new Date(model.windowStartMs).getUTCFullYear();
    const lastYear = new Date(model.windowEndMs).getUTCFullYear();
    for (let year = firstYear; year <= lastYear; year++) out.push(Date.UTC(year, 0, 1));
    return out;
  }, [granularity, model.windowStartMs, model.windowEndMs, model.monthStartsMs]);

  // Axis label subtrees, memoized on their source arrays so re-renders that
  // don't change the timeline (e.g. collapsing a group) reuse them instead of
  // reconciling hundreds/thousands of label nodes.
  const periodEls = useMemo(
    () =>
      periods.map((period) => (
        <span
          key={period.key}
          className={styles.year}
          style={{ left: `${period.startFrac * 100}%`, width: `${(period.endFrac - period.startFrac) * 100}%` }}
        >
          {period.label}
        </span>
      )),
    [periods],
  );
  const coarseGridlineEls = useMemo(
    () =>
      coarseAnchors
        .filter((ms) => ms > model.windowStartMs && ms < model.windowEndMs)
        .map((ms) => (
          <span key={ms} className={styles.gridlineCoarse} style={{ left: `${frac(ms) * 100}%` }} aria-hidden />
        )),
    [coarseAnchors, model.windowStartMs, model.windowEndMs, frac],
  );
  const tickEls = useMemo(
    () =>
      ticks.map((tick) => (
        <span key={tick.ms} className={styles.month} style={{ left: `${tick.frac * 100}%` }}>
          {tick.label}
        </span>
      )),
    [ticks],
  );

  // Collapse + two-pane scroll sync extracted into focused hooks.
  const { collapsibleKeys, collapsedKeys, visibleRows, toggle } = useMasterScheduleCollapse(model.rows);

  const pxPerDay = PX_PER_DAY[granularity];
  const trackW = `${model.windowDays * pxPerDay}px`;
  const { frozenRef, scrollerRef, todayJump, onScroll, scrollToToday } = useTimelineScrollSync(
    model.todayFrac,
    `${granularity}:${model.windowDays}`,
  );

  // Gridlines drawn ONCE as a grid-spanning overlay (not per row). Day view is
  // dense + uniform → a CSS gradient (zero nodes); week/month → memoized spans.
  const gridStepPx = granularity === 'day' ? pxPerDay : null;
  const gridlineEls = useMemo(
    () =>
      gridStepPx !== null
        ? null
        : ticks
            .filter((tick) => tick.frac !== 0)
            .map((tick) => (
              <span key={tick.ms} className={styles.gridline} style={{ left: `${tick.frac * 100}%` }} aria-hidden />
            )),
    [ticks, gridStepPx],
  );

  // Resolved once and passed to every FrozenRow so the memoized rows compare
  // equal (avoids threading the `t` function, whose identity isn't guaranteed).
  const collapseLabel = t('scheduler.rowActions.collapseTask');
  const expandLabel = t('scheduler.rowActions.expandTask');

  return (
    <BaseTooltip.Provider delay={0} closeDelay={0}>
      <div
        className={styles.panes}
        // Day view stacks three header rows (period + week band + day numbers);
        // 62px = 3×16px rows + the axis's 6+8px padding. Others use two (46px).
        style={{ '--track-w': trackW, '--ms-axis-h': granularity === 'day' ? '62px' : '46px' } as React.CSSProperties}
      >
        {/* ---- Frozen group list (left pane) — vertical scroll mirrored from
                the timeline; never scrolls horizontally. ---- */}
        <div className={styles.frozenPane} ref={frozenRef}>
          <div className={styles.frozenCorner} />
          {visibleRows.map((row) => (
            <FrozenRow
              key={row.key}
              row={row}
              collapsible={collapsibleKeys.has(row.key)}
              collapsed={collapsedKeys.has(row.key)}
              statusText={rowStatusText(row, t)}
              collapseLabel={collapseLabel}
              expandLabel={expandLabel}
              onToggle={toggle}
            />
          ))}
        </div>

        {/* ---- Timeline (right pane) — owns both scrollbars; content is clipped
                to this box, so bars/diamonds can't bleed into the group list. ---- */}
        <ScrollArea orientation="both" className={styles.scroller} viewportRef={scrollerRef} viewportProps={{ onScroll }}>
          {/* Axis: period + tick rows, sticky to the top while the rows scroll. */}
          <div className={styles.axis}>
            <div className={styles.years}>{periodEls}</div>
            {weekBandEls && <div className={styles.ticks}>{weekBandEls}</div>}
            <div className={styles.ticks}>{tickEls}</div>
          </div>

          <div className={styles.grid}>
            {/* Single gridline layer behind all rows (see gridlineEls above). */}
            <div
              className={`${styles.gridlines} ${gridStepPx !== null ? styles.gridlinesDense : ''}`}
              style={gridStepPx !== null ? ({ '--grid-step': `${gridStepPx}px` } as React.CSSProperties) : undefined}
              aria-hidden
            >
              {gridlineEls}
              {coarseGridlineEls}
            </div>
            {visibleRows.map((row) => (
              <TrackRow key={row.key} row={row} onFocus={onFocus} getOpenLabel={getOpenLabel} />
            ))}

            {/* "Today" marker — vertical dashed line across all rows, spanning
                the whole timeline pane, so `left` is an honest fraction of the
                window. Hidden when today falls outside the schedule window. */}
            {model.todayFrac !== null && (
              <div className={styles.todayOverlay} aria-hidden>
                <div className={styles.today} style={{ left: `${model.todayFrac * 100}%` }}>
                  <span className={styles.todayLabel}>{t('dashboard.masterSchedule.todayMarker')}</span>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Jump-to-today — shown only when the today line is scrolled out of the
            timeline viewport; the chevron points toward it. */}
        {todayJump && (
          <button
            type="button"
            className={`${styles.todayJump} ${todayJump === 'left' ? styles.todayJumpLeft : styles.todayJumpRight}`}
            aria-label={t('dashboard.masterSchedule.goToToday')}
            onClick={scrollToToday}
          >
            {todayJump === 'left' && <ChevronLeftIcon size={14} />}
            <span>{t('dashboard.masterSchedule.todayMarker')}</span>
            {todayJump === 'right' && <ChevronRightIcon size={14} />}
          </button>
        )}
      </div>
    </BaseTooltip.Provider>
  );
}
